import { BASE_API_URL_V2 } from "@/constants";
import { useQuery } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import type { Temporal } from "@/utils/temporal";

export interface LinkedEntity {
  public_id: string;
  kind: string;
  name: string;
}

interface FundAttributes {
  fund_name?: string;
  fund_managers?: string[];
  fund_strategy_label?: string;
  [key: string]: unknown;
}

interface Organization {
  org_type?: string;
  org_name?: string;
  org_name_fmt?: string;
  [key: string]: unknown;
}

interface Person {
  person_type?: string;
  person_name?: string;
  person_role?: string;
  organization_name?: string;
}

interface Company {
  company_name?: string;
  context?: string;
}

interface Publisher {
  organization_name?: string;
  person_name?: string;
  publication_name?: string;
}

interface Geographical {
  geo_type?: string;
  geo_name?: string;
}

interface FinancialTimePeriod {
  period_type?: string;
  period_date?: string;
}

// One copy or revision of the open document (#11956). `relation` is "copy"
// when an earlier member has the same content and "revision" when this member
// is a distinct content state; `tier` says how it was linked (1 identical
// content, 2 shared document number, 3 same Office author and creation time,
// 4 text similarity, which is only ever a suggestion and carries the
// probability, 0 to 1, that the member and the lineage are one document).
export interface LineageMember {
  blob_id: string;
  filename: string;
  filesystem_path: string[];
  // An instant: source-created for a connector document, else our ingest.
  uploaded_at: string;
  // A calendar day, or null.
  document_date: string | null;
  blob_kind: string;
  relation: "copy" | "revision";
  tier: number;
  confidence: "fact" | "suggested";
  // Set on a suggested member; null on a fact.
  probability: number | null;
  content_group: string;
  ordinal: number;
  evidence: string[];
  is_latest: boolean;
  is_current: boolean;
}

// The lineage the open document belongs to, newest member first, or null.
export interface DocumentLineage {
  lineage_id: number;
  // Null when the latest member is not visible to the viewer.
  latest_blob_id: string | null;
  member_count: number;
  members: LineageMember[];
}

// How the document arrived (#13433): the last row of its Activity panel.
// `at` is the instant the blob was stored, in the shared temporal wire shape
// (`@/utils/temporal`); `actor` is the uploading user's display name, the
// sender's address, or, for a system arrival, the connector's name ("System"
// when no connector made it).
export interface DocumentArrival {
  at: Extract<Temporal, { granularity: "instant" }>;
  kind: "upload" | "email" | "system";
  actor: string;
  // A system arrival a connector's sync made; false on every other arrival.
  connector: boolean;
}

// An email the document arrived with (#15043). `title` is the email's
// subject (its interaction's title; empty when the email had none);
// `occurred_at` is when it was sent, in the shared temporal wire shape.
export interface SourceEmail {
  blob_id: string;
  filename: string;
  title: string;
  interaction_public_id: string;
  occurred_at: Temporal;
}

export interface DocumentMetadata {
  blob_id: string;
  filename: string;
  document_types: string[];
  document_date: string | null;
  // A calendar day (#11277): the day the document takes effect or its figures
  // are good as of, or null.
  effective_date: string | null;
  document_title: string | null;
  uploaded_at: string | null;
  uploaded_by_email: string | null;
  filesystem_path: string[] | null;
  summary: string | null;
  fund_attributes: FundAttributes | null;
  // The record's fund names: its fund name, or every fund a multi-fund
  // record lists when it names none (#13708). Absent from a fixture that
  // predates it.
  fund_names?: string[];
  organization: Organization | null;
  people: Person[] | null;
  companies: Company[] | null;
  geographical: Geographical[] | null;
  publisher: Publisher | null;
  topics: string[] | null;
  financial_time_period: FinancialTimePeriod | null;
  custom_attributes: Record<string, unknown>;
  linked_entities: LinkedEntity[];
  lineage: DocumentLineage | null;
  // Absent from a fixture that predates it; null when the blob is gone.
  arrival?: DocumentArrival | null;
  // The emails that brought the document in, newest first, those the viewer
  // may open only (#15043). Empty when none did.
  source_emails: SourceEmail[];
}

const useDocumentMetadata = (blobId: string | null, enabled: boolean) => {
  return useQuery({
    queryKey: ["documentMetadata", blobId],
    queryFn: async (): Promise<DocumentMetadata> => {
      const response = await fpFetch(
        `${BASE_API_URL_V2}/api/v2/rms/documents/${blobId}/metadata`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch metadata: ${response.status}`);
      }
      return response.json();
    },
    enabled: Boolean(blobId) && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export default useDocumentMetadata;
